import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from 'store/store';
import { getRequest } from 'api/apiClient';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { TABLE_HEIGHT } from 'constants/ui';
import { DATE_TIME_AM_PM_FORMAT, DATE_TIME_FORMAT } from 'constants/dates';
import { OpTable, IOpTableProps } from 'components/customAntd/DLS/OpTable/OpTable';
import { OpTableRawColumnType } from 'components/customAntd/DLS/OpTableCore/OpTableCore';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import { formatFullName, hasPermission } from 'utils/utils';
import { getStatusNameById } from 'utils/visitorsHelper';
import { fetchVisits, setSelectedVisit, setVisitsEndDate, setVisitsStartDate } from 'store/slices/visitsSlice';
import VisitorsDrawer from 'components/pages/visitors/drawer/VisitorsDrawer';
import DateRangeLocationFilter2 from 'components/customAntd/DateRangeLocationFilter2';
import { Location } from 'types/locationTypes';
import { useTranslation } from 'react-i18next';
import { fetchMsiAltaEntries } from 'store/slices/integrationSlice';

dayjs.extend(isBetween);

interface GuestPassLogTableData {
    id: number;
    visitId: number;
    siteId: number;
    visitor: string;
    status: string;
    validFrom: string;
    validTo: string;
    requestedUtc: string;
    entryId: number;
    entry: string;
    result: string;
    location: string;
    passStatus: string;
    issuer: string;
}

const GuestPassLogTable: React.FC = () => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const msiAltaEntries = useSelector((state: RootState) => state.integration.msiAltaEntries);
    const entriesLoaded = useSelector((state: RootState) => state.integration.entriesLoaded);

    const selectedLocationIdRef = useRef<number>(globalLocationId!);
    const startDateRef = useRef(dayjs().startOf('week').format(DATE_TIME_FORMAT));
    const endDateRef = useRef(dayjs().endOf('week').format(DATE_TIME_FORMAT));

    const [guestPassLogs, setGuestPassLogs] = useState<GuestPassLogTableData[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [isVisitorsDrawerOpen, setIsVisitorsDrawerOpen] = useState(false);
    const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);

    // Permission checks
    const hasAllvisitorsRead = hasPermission(tokenScopeList, orgId, 'o', 'allvisitors:r');
    const hasGuestpassRead = hasPermission(tokenScopeList, orgId, 'o', 'guestpass:r');

    const { t } = useTranslation();

    const getPassStatus = (status: number, startUtc: string, endUtc: string): string => {
        const now = dayjs();
        return status === 1 && now.isBetween(dayjs(startUtc).utc(true), dayjs(endUtc).utc(true), null, '[]') ? 'Valid' : 'Invalid';
    };

    const fetchGuestPassLogs = async (locationsData: Location[]) => {
        setLoading(true);
        try {
            const siteParam = selectedLocationIdRef.current !== -1 ? `siteId=${selectedLocationIdRef.current}&` : '';
            const url = `/orgs/${orgId}/msiAltaGuestPass/log?${siteParam}startUtc=${startDateRef.current}&endUtc=${endDateRef.current}`;

            const guestPassLogResponse = await getRequest(url);

            const logs: GuestPassLogTableData[] = [];
            guestPassLogResponse.data.forEach((log: any) => {

                // Find the guestPass that was used in the log
                const pass = log.guestPass.find((p: any) => p.id === log.guestPassId);
                if (!pass) return null;

                // Exclude records without permission or visitors
                if (
                    !(hasAllvisitorsRead || globalUserId === pass.hostUserId) ||
                    !pass.visitor || pass.visitor.length === 0
                ) return;

                const location = locationsData.find(location => location.id === pass.siteId);

                logs.push({
                    id: log.id,
                    siteId: pass.siteId,
                    location: location?.name || '',
                    visitId: pass.visitId,
                    visitor: formatFullName(pass.visitor[0]?.firstName, pass.visitor[0]?.middleName, pass.visitor[0]?.lastName)
                        + (pass.visitor.length > 1 ? ` (+${pass.visitor.length - 1})` : ''),
                    status: getStatusNameById(pass.visitStatus, undefined),
                    validFrom: dayjs(pass.startUtc).utc(true).local().format(DATE_TIME_AM_PM_FORMAT),
                    validTo: dayjs(pass.endUtc).utc(true).local().format(DATE_TIME_AM_PM_FORMAT),
                    passStatus: getPassStatus(pass.status, pass.startUtc, pass.endUtc),
                    requestedUtc: dayjs(log.requestedUtc).utc(true).local().format(DATE_TIME_AM_PM_FORMAT),
                    entryId: log.entryId ?? '',
                    entry: msiAltaEntries.find((entry) => entry.value === log.entryId)?.label || '',
                    result: log.success === 1 ? 'Success' : 'Failed',
                    issuer: formatFullName(pass.generatedBy?.[0].firstName, null, pass.generatedBy?.[0].lastName) ?? '',
                });
            });
            setGuestPassLogs(logs);
        } catch (error) {
            console.log("Failed to fetch guest pass logs.", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchVisitsData = async () => {
        dispatch(setVisitsStartDate(undefined));
        dispatch(setVisitsEndDate(undefined));
        dispatch(fetchVisits({ orgId }));
    };

    // Fetch MSI Alta entries on mount (only if not already loaded)
    useEffect(() => {
        if (orgId && !entriesLoaded) {
            dispatch(fetchMsiAltaEntries({ orgId }));
        }
    }, [orgId, entriesLoaded, dispatch]);

    // Fetch initial data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch locations
                const locationsResponse = await getRequest(`/orgs/${orgId}/sites`, { status: 1 });
                const locationsData = locationsResponse.data;
                setLocations(locationsData);

                // Fetch guest pass logs (uses Redux msiAltaEntries)
                if (msiAltaEntries.length > 0) {
                    await fetchGuestPassLogs(locationsData);
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.error("Failed to fetch data.", error);
                setLoading(false);
            }
        };

        if (orgId && entriesLoaded) {
            fetchVisitsData();
            fetchData();
        }
        // eslint-disable-next-line
    }, [orgId, entriesLoaded]);

    // Table columns definition
    const columns: OpTableRawColumnType[] = [
        {
            dataIndex: 'visitor',
            label: t('guestPassLog.table.visitor'),
            filter: { type: 'input' },
            sorter: (a, b) => a.visitor.length - b.visitor.length
        },
        {
            dataIndex: 'requestedUtc',
            label: t('guestPassLog.table.date'),
            sorter: (a, b) => dayjs(b.requestedUtc).diff(a.requestedUtc)
        },
        {
            dataIndex: 'entry',
            label: t('guestPassLog.table.entry'),
            filter: {
                type: 'multiSelect',
                options: msiAltaEntries,
            },
            sorter: (a, b) => a.entry.localeCompare(b.entry),
            onFilter: (value, record) => String(record.entryId) === String(value),
        },
        {
            dataIndex: 'result',
            label: t('guestPassLog.table.result'),
            filter: {
                type: 'multiSelect',
                options: [
                    { label: t('guestPassLog.table.success'), value: 'Success' },
                    { label: t('guestPassLog.table.failed'), value: 'Failed' },
                ],
            },
            sorter: (a, b) => a.result.localeCompare(b.result),
            defaultFilteredValue: ['Success', 'Failed'],
            render: (value) => {
                const color = value === 'Success' ? 'green' : 'var(--colorError)';
                return (
                    <span style={{ color }}>
                        {t(`guestPassLog.table.${value.toLowerCase()}`)}
                    </span>
                );
            },
        },
        {
            dataIndex: 'location',
            label: t('guestPassLog.table.location'),
            filter: {
                type: 'multiSelect',
                options: locations.map(location => ({
                    label: location.name,
                    value: location.id,
                })),
            },
            defaultFilteredValue: locations.map(location => String(location.id)),
            sorter: (a, b) => a.location.localeCompare(b.location),
            onFilter: (value, record) => {
                return String(record.siteId) === String(value);
            },
        },
        {
            dataIndex: 'passStatus',
            label: t('guestPassLog.table.passStatus'),
            filter: {
                type: 'multiSelect',
                options: [
                    { label: t('guestPassLog.table.valid'), value: 'Valid' },
                    { label: t('guestPassLog.table.invalid'), value: 'Invalid' },
                ],
            },
            defaultFilteredValue: ['Valid', 'Invalid'],
            sorter: (a, b) => a.passStatus.localeCompare(b.passStatus),
            onFilter: (value, record) => {
                return record.passStatus === value;
            },
            render: (value) => {
                const color = value === 'Valid' ? '#709A7A' : 'grey';
                const textColor = 'white';
                const translated = value === 'Valid' ? t('guestPassLog.table.valid') : t('guestPassLog.table.invalid');

                return (
                    <div
                        style={{
                            backgroundColor: color,
                            color: textColor,
                            padding: '5px 10px',
                            borderRadius: '4px',
                            textAlign: 'center',
                        }}
                    >
                        {translated}
                    </div>
                );
            },
        },

    ];

    const handleDateRangeLocationFilter = (locationId: number, startDate: string, endDate: string) => {
        selectedLocationIdRef.current = locationId;
        startDateRef.current = startDate;
        endDateRef.current = endDate;
        fetchGuestPassLogs(locations);
    };

    const handleVisitClick = async (visit: any) => {
        const response = await getRequest(`/orgs/${orgId}/visitor/${visit.visitId}`);
        const selectedVisit = response.data[0];
        dispatch(setSelectedVisit(selectedVisit!));
        setIsVisitorsDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setIsVisitorsDrawerOpen(false);
        dispatch(setSelectedVisit(null));
    };

    // Table properties
    const opTableProps: IOpTableProps = {
        dataSource: guestPassLogs,
        label: t('guestPassLog.table.label'),
        columns: columns,
        rowActions: {
            onEditClick: handleVisitClick,
        },
        height: TABLE_HEIGHT,
        allowGlobalSearch: true,
        allowExport: true,
        allowShowHideColumns: true,
        loading: loading,
        writeAccess: false,
        gtm: 'guest-pass-log-table-gtm',
        rowKey: 'id',
    };

    return (
        <OpSpace direction="vertical" size="middle" style={{ display: 'flex' }}>
            <DateRangeLocationFilter2
                onDateRangeLocationFilter={handleDateRangeLocationFilter}
                initialStartDate={startDateRef.current}
                initialEndDate={endDateRef.current}
            />

            <OpTable {...opTableProps} />

            {(isVisitorsDrawerOpen) && (
                <VisitorsDrawer
                    open={isVisitorsDrawerOpen}
                    isProfileDrawerOpen={isProfileDrawerOpen}
                    onClose={handleDrawerClose}
                    setIsProfileDrawerOpen={setIsProfileDrawerOpen}
                    tab={hasGuestpassRead ? 'guestPassLog' : 'visitors'}
                />
            )}
        </OpSpace>
    );
}

export default GuestPassLogTable;